import { Modal } from 'antd';
import type { TabDefinition } from 'components/Tabs/Tabs';
import Tabs from 'components/Tabs/Tabs';
import React from 'react';
import { DestinationEditorBasicInfo } from './DestinationEditorBasicInfo';
import { useDestinationEditor } from './DestinationEditorContext';
import { DestinationEditorHighlights } from './DestinationEditorHighlights';
import { DestinationEditorGoodToKnow } from './DestinationEditorGoodToKnow';
import { DestinationEditorItinerary } from './DestinationEditorItinerary';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    DestinationEditorTabActionsProvider,
    DestinationEditorTabActionsSlot,
} from './DestinationEditorTabActions';

const { confirm } = Modal;

export function DestinationEditor() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { isEditing, isCountry } = useDestinationEditor();

    // Get active tab from URL parameter or default to 'basicInfo'
    const activeTab = searchParams.get('tab') || 'basicInfo';

    // Update URL when tab changes
    const handleTabChange = (tabKey: string) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', tabKey);

        // Use navigate to preserve the location state (returnTo)
        navigate(`${location.pathname}?${newSearchParams.toString()}`, {
            replace: true,
            state: location.state,
        });
    };

    const handleDiscard = () => {
        if (location.state?.returnTo) {
            navigate(location.state.returnTo);
        } else {
            navigate('/destinations/overview');
        }
    };

    const handleClickDiscard = (hasUnsavedChanges: boolean) => {
        if (!hasUnsavedChanges) {
            handleDiscard();
            return;
        }

        confirm({
            title: 'Discard changes',
            content:
                'Are you sure you want to discard the changes you made in the destination? All changes will be lost.',
            onOk: handleDiscard,
            okText: 'Discard',
            okButtonProps: {
                danger: true,
            },
            cancelText: 'Cancel',
        });
    };

    // For country destinations, only show Basic Info tab
    // For locality destinations, show all tabs when editing
    const tabs: TabDefinition[] = [
        {
            name: 'Basic Info',
            key: 'basicInfo',
            content: (
                <DestinationEditorBasicInfo
                    onClickDiscard={handleClickDiscard}
                />
            ),
        },
        {
            name: 'Highlights',
            key: 'highlights',
            content: (
                <DestinationEditorHighlights
                    onClickDiscard={handleClickDiscard}
                />
            ),
            isHidden: !isEditing || isCountry,
        },
        {
            name: 'Good to Know',
            key: 'goodToKnow',
            content: (
                <DestinationEditorGoodToKnow
                    onClickDiscard={handleClickDiscard}
                />
            ),
            isHidden: !isEditing || isCountry,
        },
        {
            name: 'Itinerary',
            key: 'itinerary',
            content: (
                <DestinationEditorItinerary
                    onClickDiscard={handleClickDiscard}
                />
            ),
            isHidden: !isEditing || isCountry,
        },
    ];

    return (
        <DestinationEditorTabActionsProvider activeTab={activeTab}>
            <Tabs
                defaultActiveKey="basicInfo"
                type="full-page"
                tabs={tabs}
                activeKey={activeTab}
                onChange={handleTabChange}
                tabBarExtraContent={<DestinationEditorTabActionsSlot />}
            />
        </DestinationEditorTabActionsProvider>
    );
}
